import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../config';

function apiOriginFromApiBase(apiBase) {
  // API_BASE is typically ".../api". We need the origin to navigate to backend auth URLs like "/api/auth/...".
  return String(apiBase || '').replace(/\/api\/?$/, '');
}

function withQueryParam(url, key, value) {
  try {
    const u = new URL(String(url));
    u.searchParams.set(key, value);
    return u.toString();
  } catch {
    return url;
  }
}

export default function StravaFormatCard({ workoutId, canEdit, debrief, formattedAt }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reauthUrl, setReauthUrl] = useState(null);
  const [doneAt, setDoneAt] = useState(null);

  const hasStravaActivity = Boolean(debrief?.strava_activity_id);
  const apiOrigin = useMemo(() => apiOriginFromApiBase(API_BASE), []);

  const runFormat = async ({ allowModal } = { allowModal: true }) => {
    if (!workoutId || !hasStravaActivity) return;
    setLoading(true);
    setError(null);
    try {
      const redirectTo = withQueryParam(
        withQueryParam(window.location.href, 'strava_after', 'strava_format'),
        'workout_id',
        String(workoutId)
      );
      const res = await fetch(`${API_BASE}/workouts/${workoutId}/format-strava`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ redirect_to: redirectTo })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403 && data?.code === 'STRAVA_SCOPE_MISSING' && data?.reauth_url && allowModal) {
          setReauthUrl(String(data.reauth_url));
          setShowModal(true);
          return;
        }
        throw new Error(data?.error || t('workout.strava_format_error'));
      }
      setDoneAt(new Date().toISOString());
    } catch (e) {
      setError(e?.message || t('workout.strava_format_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // After returning from Strava OAuth callback, we land back with ?strava=ok.
    // If the user intent was to format Strava, auto-run once.
    const params = new URLSearchParams(window.location.search);
    const strava = params.get('strava');
    const after = params.get('strava_after');
    const wid = params.get('workout_id');
    if (strava === 'ok' && after === 'strava_format' && String(wid || '') === String(workoutId || '')) {
      runFormat({ allowModal: false });
      params.delete('strava');
      params.delete('strava_after');
      params.delete('workout_id');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId]);

  if (!hasStravaActivity) return null;

  const effectiveFormattedAt = formattedAt || doneAt || null;
  const formattedLabel = effectiveFormattedAt ? new Date(effectiveFormattedAt).toLocaleString() : null;
  const isDone = Boolean(effectiveFormattedAt);

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5 relative">
      {isDone ? (
        <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-extrabold text-green-800 bg-green-50 border border-green-200 whitespace-nowrap flex items-center gap-2">
          <span>✅</span>
          <span>{t('workout.strava_format_updated')}</span>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
            {t('workout.strava_format_title')}
          </div>
          <div className="text-sm text-gray-700 mt-2">
            {t('workout.strava_format_explain')}
          </div>
          {error ? (
            <div className="text-xs text-red-600 font-bold mt-2">
              {error}
            </div>
          ) : null}
        </div>

        {!isDone ? (
          <button
            type="button"
            disabled={!canEdit || loading}
            onClick={() => runFormat()}
            className={[
              'w-full sm:w-auto px-4 py-2 rounded-lg font-extrabold text-white',
              loading ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700',
              !canEdit ? 'opacity-60 cursor-not-allowed' : ''
            ].join(' ')}
          >
            {loading ? '...' : t('workout.strava_format_cta')}
          </button>
        ) : null}
      </div>

      {showModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('workout.strava_format_need_permission_title')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('workout.strava_format_need_permission_body')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowModal(false); setReauthUrl(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                {t('workout.strava_format_cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!reauthUrl) return;
                  const full = reauthUrl.startsWith('http') ? reauthUrl : `${apiOrigin}${reauthUrl}`;
                  window.location.href = full;
                }}
                className="px-4 py-2 rounded text-white font-bold bg-orange-600 hover:bg-orange-700"
              >
                {t('workout.strava_format_continue')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
